import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { loginDiwali, startDiwali } from "./diwaliSlice";
import DiwaliHeader from "./DiwaliHeader";
import "./diwali.css";

const ASSET_BASE = "/diwaliEscapeRoom";
// Intro videos (desktop + portrait) are served from S3 instead of the local
// public/ bundle — same file names, uploaded directly under this game's S3
// folder (no "intro/" subpath there, unlike the local copy).
const S3_VIDEO_BASE = process.env.REACT_APP_S3_PATH;

// glowstone-old/index.php equivalent: runs login (session_start() +
// login-default.php there), plays the intro video, and shows a "Begin Play"
// button once logged in — exactly matching the legacy page's combined
// login+intro behavior instead of this platform's separate /login page.
// Uses its own self-contained loginDiwali thunk (diwaliSlice.js), not the
// shared loginUser thunk — that one also fetches theme/admin data this game
// has none of.
export default function DiwaliIndex() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    authStatus: status,
    user,
    authError: error,
    id: gameRowId,
    status: statStatus,
    completed,
  } = useSelector((state) => state.diwali);
  const videoRef = useRef(null);
  const startDiwaliAttemptedRef = useRef(false);
  const [isPortrait, setIsPortrait] = useState(
    () => window.innerHeight > window.innerWidth
  );

  useEffect(() => {
    dispatch(loginDiwali());
  }, [dispatch]);

  useEffect(() => {
    if (status !== "failed") return;
    Swal.fire("Session Expired!", error ? String(error) : "Session expired", "error");
  }, [status, error]);

  // Fetch this user's stat row as soon as they're logged in — same call
  // DiwaliMain/DiwaliGame make — so an already-completed user (by userId,
  // via the resumed session) sees this before ever pressing Begin Play,
  // instead of only finding out after the video/main-popups play through.
  //
  // Gated by a ref, not `statStatus !== "loading"` — that condition is also
  // true once a rejected attempt settles into status "failed", and with
  // `statStatus` in the dependency array that retried on every single
  // failure, forever, hammering the backend once a second for as long as it
  // kept failing. One attempt per mount.
  useEffect(() => {
    if (user && !gameRowId && !startDiwaliAttemptedRef.current) {
      startDiwaliAttemptedRef.current = true;
      dispatch(startDiwali());
    }
  }, [user, gameRowId, dispatch]);

  useEffect(() => {
    if (statStatus === "succeeded" && completed) {
      Swal.fire(
        "Already Completed",
        "You have already completed this game.",
        "info"
      );
    }
  }, [statStatus, completed]);

  useEffect(() => {
    const check = () => setIsPortrait(window.innerHeight > window.innerWidth);
    check();
    // Event-driven instead of a 1 s poll: resize covers window/URL-bar changes
    // and orientationchange covers rotation on devices that don't fire resize.
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);
    return () => {
      window.removeEventListener("resize", check);
      window.removeEventListener("orientationchange", check);
    };
  }, []);

  const goToMain = useCallback(() => navigate("/diwali/main"), [navigate]);

  const loggedIn = status === "succeeded" && Boolean(user);

  return (
    <div className="dw-index-root">
      <DiwaliHeader />
      <video
        ref={videoRef}
        className="dw-index-video"
        src={`${S3_VIDEO_BASE}${isPortrait ? "Diwali_EscapeRoom_portrait.mp4" : "Diwali_EscapeRoom.mp4"}`}
        muted
        autoPlay
        playsInline
        preload="metadata"
        onEnded={() => {
          if (loggedIn) goToMain();
        }}
      />

      {loggedIn && (
        <div className="dw-begin-wrap">
          <img
            src={`${ASSET_BASE}/images/BeginPlay.png`}
            alt="Begin Play"
            className="dw-begin-btn"
            onClick={goToMain}
          />
        </div>
      )}
    </div>
  );
}
